// src/features/dashboard/hooks/useDashboardSearch.ts
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@shared/hooks/useDebounce';
import { fetchDashboardItems } from '../services/dashboard';
import {
    clearIncludeThirdParty,
    readIncludeThirdParty,
    writeIncludeThirdParty,
} from '../utils/includeThirdPartyStorage';
import type { DashboardFilters } from '../types/filters.types';

export const DASHBOARD_SEARCH_DEBOUNCE_MS = 500;

interface SortConfig {
    sort_by?: string;
    sort_direction?: 'asc' | 'desc';
}

interface PaginationConfig {
    page: number;
    per_page: number;
}

interface UseDashboardSearchOptions {
    searchThreshold?: number; // minimum chars before search
    debounceMs?: number;
}

export const useDashboardSearch = (options: UseDashboardSearchOptions = {}) => {
    const { searchThreshold = 3, debounceMs = DASHBOARD_SEARCH_DEBOUNCE_MS } = options;

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [sortConfig, setSortConfig] = useState<SortConfig>({});
    const [pagination, setPagination] = useState<PaginationConfig>({
        page: 1,
        per_page: 20,
    });
    const [filters, setFilters] = useState<DashboardFilters>({});
    const [includeThirdParty, setIncludeThirdParty] = useState(readIncludeThirdParty);

    const debouncedSearchTerm = useDebounce(searchTerm, debounceMs);
    const trimmedTerm = debouncedSearchTerm.trim();
    const hasSearchTerm = trimmedTerm.length >= searchThreshold;

    const queryParams = useMemo(
        () => ({
            ...(hasSearchTerm && { search: trimmedTerm }),
            ...(includeThirdParty && { include_third_party: true }),
            ...(sortConfig.sort_by && sortConfig),
            ...filters,
            page: pagination.page,
            per_page: pagination.per_page,
        }),
        [trimmedTerm, hasSearchTerm, includeThirdParty, sortConfig, pagination, filters],
    );

    const query = useQuery({
        queryKey: ['dashboard', 'search', queryParams],
        queryFn: () => fetchDashboardItems(queryParams),
        placeholderData: (previousData) => previousData,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        retry: 2,
        // Re-fetch when params change
        enabled: true,
    });

    // Keep the memo: `query.data?.items ?? []` would hand DataTable a fresh array identity on
    // every render while data is undefined.
    const items = useMemo(() => query.data?.items ?? [], [query.data]);
    const meta = query.data?.meta;
    const thirdPartyStatus = query.isPlaceholderData
        ? 'not_applicable'
        : (query.data?.thirdParty ?? 'not_applicable');

    const clearFilters = () => {
        setFilters({});
        setPagination({ page: 1, per_page: 20 });
    };

    const resetSearch = () => {
        setSearchTerm('');
        setSortConfig({});
        setIncludeThirdParty(false);
        clearIncludeThirdParty();
        setPagination({ page: 1, per_page: 20 });
        clearFilters();
    };

    const refreshSearch = () => {
        query.refetch();
    };

    const handleFiltersChange = (newFilters: DashboardFilters) => {
        setFilters(newFilters);
        setPagination((prev) => ({ ...prev, page: 1 }));
    };

    const handleIncludeThirdPartyChange = (checked: boolean) => {
        setIncludeThirdParty(checked);
        writeIncludeThirdParty(checked);
        setPagination((prev) => ({ ...prev, page: 1 }));
    };

    const handleSort = (column: string) => {
        setPagination((prev) => ({ ...prev, page: 1 }));
        setSortConfig((prev) => {
            if (prev.sort_by === column) {
                return {
                    sort_by: column,
                    sort_direction: prev.sort_direction === 'asc' ? 'desc' : 'asc',
                };
            }
            return {
                sort_by: column,
                sort_direction: 'asc',
            };
        });
    };

    const handlePageChange = (page: number) => {
        setPagination((prev) => ({ ...prev, page }));
    };

    const handlePerPageChange = (per_page: number) => {
        setPagination({ page: 1, per_page });
    };

    return {
        items,
        meta,
        thirdPartyStatus,
        isLoading: query.isLoading,
        error: query.error,
        isFetching: query.isFetching,
        resetSearch,
        refreshSearch,
        searchTerm,
        setSearchTerm,
        debouncedSearchTerm,
        sortConfig,
        handleSort,
        pagination,
        handlePageChange,
        handlePerPageChange,
        filters,
        handleFiltersChange,
        clearFilters,
        includeThirdParty,
        handleIncludeThirdPartyChange,
    };
};
